<template>
  <CustomTitleBar />
  <div class="login-container">
    <h2 class="title">验证码登录</h2>

    <!-- 手机号输入框 -->
    <div class="input-group">
      <van-field v-model="phoneNumber" placeholder="输入手机号" :border="false">
        <template #label>
          <div class="country-code" @click="showAreaCodePicker = true">
            <SvgIcon name="phone" size="16" />
            <span>+{{ phoneAreaPrefix }}</span>
            <van-icon name="arrow-down" size="14" color="#666666" />
          </div>
        </template>
      </van-field>
    </div>

    <!-- 图形验证码 -->
    <div class="input-group">
      <van-field v-model="captcha" placeholder="输入图形验证码" :border="false">
        <template #label>
          <SvgIcon name="code" size="16" />
        </template>
        <template #right-icon>
          <el-image class="captcha-img" :src="captchaUrl" @click="refreshCaptcha" />
        </template>
      </van-field>
    </div>

    <!-- 验证码输入框 -->
    <div class="input-group">
      <van-field v-model="verificationCode" placeholder="输入验证码" :border="false">
        <template #label>
          <SvgIcon name="code2" size="16" />
        </template>
        <template #right-icon>
          <van-button size="small" plain type="primary" @click="sendVerificationCode" :disabled="cooldownTime > 0">
            {{ cooldownTime > 0 ? `${cooldownTime}s` : '获取验证码' }}
          </van-button>
        </template>
      </van-field>
    </div>

    <!-- 登录按钮 -->
    <van-button type="primary" block round @click="submitForm">登录</van-button>

    <!-- 协议勾选 -->
    <div class="agreement">
      <van-checkbox v-model="agreed" shape="round" icon-size="12">
        登录即表示同意<a href="https://www.jingchat.cn/userProtocol.html" target="_blank">《服务条款》</a>和<a
          href="https://www.hnmengyuqianfang.cn/features.html" target="_blank ">《隐私协议》</a>
      </van-checkbox>
    </div>

    <!-- 添加区号选择器弹出层 -->
    <van-popup v-model:show="showAreaCodePicker" position="bottom">
      <van-picker 
        :columns="areaCodeColumns" 
        @confirm="onAreaCodeConfirm" 
        @cancel="showAreaCodePicker = false"
        :title="'选择区号'" 
        show-toolbar 
      />
    </van-popup>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { showToast } from 'vant'
import CustomTitleBar from "@/components/CustomTitleBar/index.vue";
import { getCaptcha, sendSmsCode } from '@/api/system'

const store = useStore()

const captcha = ref('')
const captchaUrl = ref('')

// 刷新图形验证码
const refreshCaptcha = async () => {
  try {
    const res = await getCaptcha()
    console.log('res', res);
    captchaUrl.value = URL.createObjectURL(res)
  } catch (error) {
    console.error('验证码获取失败：', error)
  }
}

// 定义表单数据
const phoneAreaPrefix = ref('86')
const phoneNumber = ref('')
const verificationCode = ref('')
const agreed = ref(false)

// 添加倒计时相关变量
const cooldownTime = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

// 表单校验规则
const validatePhoneNumber = () => {
  if (!phoneNumber.value) {
    showToast('请输入手机号')
    return false
  }
  if (!/^1[3-9]\d{9}$/.test(phoneNumber.value)) {
    showToast('请输入正确的手机号')
    return false
  }
  return true
}

const validateVerificationCode = () => {
  if (!verificationCode.value) {
    showToast('请输入验证码')
    return false
  }
  return true
}

const validateAgreement = () => {
  if (!agreed.value) {
    showToast('请先同意隐私政策')
    return false
  }
  return true
}

// 提交表单
const submitForm = () => {
  if (!validatePhoneNumber()) return
  if (!validateVerificationCode()) return
  if (!validateAgreement()) return

  const data = {
    captcha: verificationCode.value,
    password: '',
    phoneAreaPrefix: phoneAreaPrefix.value,
    phoneNumber: phoneNumber.value,
    type: 1
  }
  store.dispatch('user/login', data)
}

// 发送验证码
const sendVerificationCode = () => {
  if (!validatePhoneNumber()) return
  if (cooldownTime.value > 0) return

  try {
    sendSmsCode({
      phoneAreaPrefix: phoneAreaPrefix.value,
      phoneNumber: phoneNumber.value,
      captcha: captcha.value,
      type: 0,
    }).then(res => {
      if (res.code === 200) {
        showToast('发送成功')
        // 开始倒计时
        cooldownTime.value = 60
        timer = setInterval(() => {
          cooldownTime.value--
          if (cooldownTime.value <= 0) {
            if (timer) {
              clearInterval(timer)
              timer = null
            }
          }
        }, 1000)
      } else {
        showToast(res.msg)
        refreshCaptcha()
      }
    })
  } catch (error) {
    console.error(error)
  }
}

const showAreaCodePicker = ref(false)

// 区号数据
const areaCodeColumns = [
  { text: '中国 +86', value: '86' },
  { text: '中国香港 +852', value: '852' },
  { text: '中国澳门 +853', value: '853' },
  { text: '中国台湾 +886', value: '886' },
  { text: '日本 +81', value: '81' },
  { text: '韩国 +82', value: '82' },
  { text: '美国 +1', value: '1' },
]

// 区号选择确认处理函数
const onAreaCodeConfirm = (item: any) => {
  phoneAreaPrefix.value = item.selectedValues[0]
  showAreaCodePicker.value = false
}

onMounted(() => {
  refreshCaptcha()
})

onUnmounted(() => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  if (captchaUrl.value) {
    URL.revokeObjectURL(captchaUrl.value)
  }
})
</script>

<style lang="scss" scoped>
.login-container {
  min-height: 100vh;
  background-color: #fff;
  padding: 20px 32px;
  box-sizing: border-box;

  .title {
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    margin: 40px 0;
  }

  .input-group {
    background: transparent;
    border-bottom: 1px solid #ebedf0;
    border-radius: 0;
    margin-bottom: 16px;
    overflow: hidden;

    .captcha-img {
      width: 100px;
      height: 32px;
      cursor: pointer;
    }

    :deep(.van-field) {
      background: transparent;

      &::after {
        display: none;
      }
    }

    :deep(.van-field__label) {
      width: auto;
      display: flex;
      align-items: center;
    }
  }

  .country-code {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-right: 8px;
    border-right: 1px solid #ebedf0;
    margin-right: 8px;

    span {
      font-size: 14px;
      color: #333;
      font-weight: normal;
    }
  }

  .action-links {
    display: flex;
    justify-content: space-between;
    margin: 16px 0 24px;

    span {
      color: #666;
      font-size: 14px;
    }
  }

  .agreement {
    text-align: center;
    font-size: 12px;
    color: #999;
    margin: 16px 0;

    :deep(.van-checkbox__label) {
      color: #999;
    }

    a {
      color: $primary-color;
      text-decoration: none;
    }
  }

  .verification-login {
    text-align: center;
    margin-top: 60px;

    .van-icon {
      display: block;
      margin: 0 auto 8px;
    }

    span {
      font-size: 14px;
      color: #666;
    }
  }
}
</style>
